import { useCallback } from 'react';

import type { ApiOrgId } from 'api/types/ApiTypedId';
import { useKeyedState } from 'hooks/useKeyedState';
import { useOrgSavedToast } from 'modules/listing/orgs/hooks/useOrgSavedToast';
import type { SearchSavedListingsStore } from 'modules/search/zustand-stores/searchSavedListingsStore';

import { useSaveListing } from './useSaveListing';

type Args = {
  orgId: ApiOrgId | null;
  orgName: string | null;
  orgType: string | null;
  orgInitiallySaved: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
};

export function useSaveOrgAfterListing({
  orgId,
  orgName,
  orgType,
  orgInitiallySaved,
  searchSavedListingsStore,
}: Args) {
  const [shouldShowSaveOrgTooltip, setShouldShowSaveOrgTooltip] = useKeyedState(
    false,
    { key: orgId ?? '' },
  );

  const showSaveOrgTooltip = useCallback(() => {
    setShouldShowSaveOrgTooltip(true);
  }, [setShouldShowSaveOrgTooltip]);

  const dismissSaveOrgTooltip = useCallback(() => {
    setShouldShowSaveOrgTooltip(false);
  }, [setShouldShowSaveOrgTooltip]);

  const { showOrgSavedToast } = useOrgSavedToast();

  const {
    saved: orgSaved,
    save: saveOrg,
    unsave: unsaveOrg,
  } = useSaveListing({
    listingType: 'ORG',
    listingId: orgId ?? null,
    listingName: orgName ?? '',
    initiallySaved: orgInitiallySaved,
    orgId,
    orgType,
    onSaved: () => {
      showOrgSavedToast();
      dismissSaveOrgTooltip();
    },
    onUnsaved: null,
    trackClickEvent: false,
    searchSavedListingsStore: searchSavedListingsStore,
  });

  return {
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    showSaveOrgTooltip,
    dismissSaveOrgTooltip,
  };
}
